// src/runtime/homeLoop.ts
// Absorbed from runtime/homeLoop.js (Task 37)
'use strict';

import type { Item, InstallerFlow, InstallConveyor } from '../types';

interface SelectorConfig {
  hysteresisPx?: number;
  ringSwitchBiasPx?: number;
  middleRingBonus?: number;
  middleRingIndex?: number;
  maxDistMinPx?: number;
  maxDistPaddingPx?: number;
  releaseBonusPx?: number;
}

export interface HomeInteractionState {
  items: Item[];
  highlighted: Item | null;
  cx: number;
  cy: number;
  cameraX: number;
  cameraY: number;
  menuScale: number;
  worldZoom: number;
  lineCenterRadius: number;
  setHighlight: (item: Item, active: boolean) => void;
  scrambleToText: (text: string) => void;
  getHomeIdleReticleText: () => string;
  hasHaptics: boolean;
  pulseHaptics: ((v: number) => void) | null;
  highlightSize: number;
  selectorConfig?: SelectorConfig;
}

export interface HomeInputCtx {
  inputActive: boolean;
}

interface HomeInteractionResult {
  highlighted: Item | null;
  focusX: number;
  focusY: number;
  centerZoneActive: boolean;
}

interface InstallerFlowState {
  updateInstallerFlow: (now: number) => void;
  updateInstallerScannerMorph: (now: number) => void;
  installerItem: Item | null;
  installConveyor: InstallConveyor | null;
  installerFlow: InstallerFlow | null;
  pairGap: number;
  includeConveyorObstacles: boolean;
  getInstallerScannerObstacleRadius: (item: Item | null) => number;
  getOrganismItemRadius: (item: Item, scale: number) => number;
  isInstallerFlowActive: () => boolean;
}

interface InstallerObstacle {
  x: number;
  y: number;
  radius: number;
  gap: number;
}

interface InstallerFrameResult {
  obstacles: InstallerObstacle[];
  spacingObstacle: InstallerObstacle | null;
  isInstallerActive: boolean;
}

interface CandidateCtx {
  cx: number;
  cy: number;
  cameraX: number;
  cameraY: number;
  sceneScale: number;
  highlighted: Item | null;
  ringSwitchBiasPx: number;
  middleRingBonus: number;
  middleRingIndex: number | null;
  maxDistMinPx: number;
  maxDistPaddingPx: number;
  releaseBonusPx: number;
}

function getItemWorldPosition(item: Item): { x: number; y: number } {
  const x = Number.isFinite(item.currentX) ? item.currentX : item.baseX;
  const y = Number.isFinite(item.currentY) ? item.currentY : item.baseY;
  return { x, y };
}

function getItemSelectionScale(item: Item): number {
  let scale = Number.isFinite(item.currentScale) && item.currentScale > 0 ? item.currentScale : 1;
  if (item.scannerActive) {
    const scannerScale = Number.isFinite(item.scannerScale) ? item.scannerScale : 1;
    scale *= Math.max(1, scannerScale);
  }
  return Math.max(scale, 0.001);
}

function getSelectableCandidateScore(item: Item, ctx: CandidateCtx): { item: Item; score: number; dist: number; dx: number; dy: number; acquireRadius: number; releaseRadius: number; itemVisualRadiusPx: number } {
  const world = getItemWorldPosition(item);
  const offsetX = (world.x - ctx.cx) * ctx.sceneScale - ctx.cameraX;
  const offsetY = (world.y - ctx.cy) * ctx.sceneScale - ctx.cameraY;
  const dist = Math.hypot(offsetX, offsetY);
  let score = dist;
  const itemBaseSize = Number.isFinite(item.size) ? item.size : 0;
  const itemVisualRadiusPx = Math.max(1, itemBaseSize * 0.5 * getItemSelectionScale(item) * ctx.sceneScale);
  const acquireRadius = Math.max(ctx.maxDistMinPx, itemVisualRadiusPx + ctx.maxDistPaddingPx);
  const releaseRadius = acquireRadius + ctx.releaseBonusPx;

  if (
    Number.isFinite(ctx.middleRingIndex)
    && item.ringIndex === ctx.middleRingIndex
    && Number.isFinite(ctx.middleRingBonus)
    && ctx.middleRingBonus > 0
  ) {
    score *= Math.max(0.65, 1 - ctx.middleRingBonus);
  }

  if (
    ctx.highlighted
    && item !== ctx.highlighted
    && Number.isFinite(ctx.ringSwitchBiasPx)
    && ctx.ringSwitchBiasPx > 0
    && Number.isFinite(item.ringIndex)
    && Number.isFinite(ctx.highlighted.ringIndex)
    && item.ringIndex !== ctx.highlighted.ringIndex
  ) {
    score += ctx.ringSwitchBiasPx;
  }

  return {
    item,
    score,
    dist,
    dx: -offsetX,
    dy: -offsetY,
    acquireRadius,
    releaseRadius,
    itemVisualRadiusPx,
  };
}

function computeNearestSelectable(items: Item[], ctx: CandidateCtx): ReturnType<typeof getSelectableCandidateScore> | null {
  let nearest = null;
  let minScore = Infinity;
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (!item || item.installState === 'inserting') continue;

    const candidate = getSelectableCandidateScore(item, ctx);
    if (candidate.score < minScore) {
      minScore = candidate.score;
      nearest = candidate;
    }
  }
  return nearest;
}

function computeInteraction(state: HomeInteractionState, inputCtx: HomeInputCtx): HomeInteractionResult {
  const items = state.items;
  let highlighted = state.highlighted;
  const cx = state.cx;
  const cy = state.cy;
  const cameraX = state.cameraX;
  const cameraY = state.cameraY;
  const menuScale = state.menuScale;
  const worldZoom = state.worldZoom;
  const lineCenterRadius = state.lineCenterRadius;

  const setHighlight = state.setHighlight;
  const scrambleToText = state.scrambleToText;
  const getHomeIdleReticleText = state.getHomeIdleReticleText;
  const hasHaptics = !!state.hasHaptics;
  const pulseHaptics = state.pulseHaptics;
  const selectorConfig = state.selectorConfig || {};
  const hysteresisPx: number = Number.isFinite(selectorConfig.hysteresisPx) ? (selectorConfig.hysteresisPx as number) : 14;
  const ringSwitchBiasPx: number = Number.isFinite(selectorConfig.ringSwitchBiasPx) ? (selectorConfig.ringSwitchBiasPx as number) : 0;
  const middleRingBonus: number = Number.isFinite(selectorConfig.middleRingBonus) ? (selectorConfig.middleRingBonus as number) : 0;
  const middleRingIndex: number | null = Number.isFinite(selectorConfig.middleRingIndex) ? (selectorConfig.middleRingIndex as number) : null;
  const maxDistMinPx: number = Number.isFinite(selectorConfig.maxDistMinPx) ? (selectorConfig.maxDistMinPx as number) : 96;
  const maxDistPaddingPx: number = Number.isFinite(selectorConfig.maxDistPaddingPx) ? (selectorConfig.maxDistPaddingPx as number) : 28;
  const releaseBonusPx: number = Number.isFinite(selectorConfig.releaseBonusPx) ? (selectorConfig.releaseBonusPx as number) : 18;

  const inputActive = !!inputCtx.inputActive;
  const resolvedMenuScale = Number.isFinite(menuScale) ? menuScale : 1;
  const resolvedWorldZoom = Number.isFinite(worldZoom) ? worldZoom : 1;
  const sceneScale = Math.max(0.001, resolvedMenuScale * resolvedWorldZoom);

  const camDist = Math.hypot(cameraX, cameraY);
  let nearest = null;

  if (inputActive && camDist >= lineCenterRadius) {
    const nearestResult = computeNearestSelectable(items, {
      cx,
      cy,
      cameraX,
      cameraY,
      sceneScale,
      highlighted,
      ringSwitchBiasPx,
      middleRingBonus,
      middleRingIndex,
      maxDistMinPx,
      maxDistPaddingPx,
      releaseBonusPx,
    });
    nearest = nearestResult && nearestResult.dist <= nearestResult.acquireRadius
      ? nearestResult.item
      : null;

    let highlightedResult = null;
    let highlightedWithinRelease = false;
    if (highlighted && highlighted.installState !== 'inserting') {
      highlightedResult = getSelectableCandidateScore(highlighted, {
        cx,
        cy,
        cameraX,
        cameraY,
        sceneScale,
        highlighted,
        ringSwitchBiasPx,
        middleRingBonus,
        middleRingIndex,
        maxDistMinPx,
        maxDistPaddingPx,
        releaseBonusPx,
      });
      highlightedWithinRelease = !!(highlightedResult && highlightedResult.dist <= highlightedResult.releaseRadius);
    }

    if (highlighted && highlightedResult && !nearest && highlightedWithinRelease) {
      nearest = highlighted;
    }

    if (highlighted && nearest && nearest !== highlighted && highlightedResult && highlightedWithinRelease) {
      if (nearestResult && nearestResult.score >= highlightedResult.score - hysteresisPx) {
        nearest = highlighted;
      }
    }
  }

  if (nearest !== highlighted) {
    if (highlighted && typeof setHighlight === 'function') {
      setHighlight(highlighted, false);
    }
    highlighted = nearest;
    if (highlighted) {
      if (typeof setHighlight === 'function') {
        setHighlight(highlighted, true);
      }
      if (typeof scrambleToText === 'function') {
        scrambleToText(' ' + highlighted.name + ' ');
      }
      if (hasHaptics && typeof pulseHaptics === 'function') {
        pulseHaptics(0.2);
      }
    } else if (typeof scrambleToText === 'function' && typeof getHomeIdleReticleText === 'function') {
      scrambleToText(getHomeIdleReticleText());
    }
  }

  if (!highlighted && typeof scrambleToText === 'function' && typeof getHomeIdleReticleText === 'function') {
    scrambleToText(getHomeIdleReticleText());
  }

  return {
    highlighted,
    focusX: highlighted ? highlighted.baseX : cx,
    focusY: highlighted ? highlighted.baseY : cy,
    centerZoneActive: camDist < lineCenterRadius,
  };
}

function stepInstallerFlow(state: InstallerFlowState, now: number): InstallerFrameResult {
  if (typeof state.updateInstallerFlow === 'function') {
    state.updateInstallerFlow(now);
  }
  if (typeof state.updateInstallerScannerMorph === 'function') {
    state.updateInstallerScannerMorph(now);
  }

  const obstacles: InstallerObstacle[] = [];
  let spacingObstacle = null;
  const includeConveyorObstacles = !!state.includeConveyorObstacles;

  if (state.installerItem && state.installerItem.scannerActive) {
    spacingObstacle = {
      x: state.installerItem.baseX,
      y: state.installerItem.baseY,
      radius: state.getInstallerScannerObstacleRadius(state.installerItem),
      gap: state.installerFlow && Number.isFinite(state.installerFlow.scannerObstacleGap)
        ? state.installerFlow.scannerObstacleGap
        : (state.pairGap || 12),
    };
    obstacles.push(spacingObstacle);
  }

  if (includeConveyorObstacles && state.installConveyor) {
    if (state.installConveyor.tempItem) {
      const tempItem = state.installConveyor.tempItem;
      obstacles.push({
        x: tempItem.currentX,
        y: tempItem.currentY,
        radius: state.getOrganismItemRadius(tempItem, tempItem.currentScale),
        gap: state.pairGap || 12,
      });
    }

    if (state.installConveyor.crowd && Array.isArray(state.installConveyor.crowd.items)) {
      for (const crowdItem of state.installConveyor.crowd.items) {
        if (!crowdItem) continue;
        obstacles.push({
          x: crowdItem.currentX,
          y: crowdItem.currentY,
          radius: state.getOrganismItemRadius(crowdItem, crowdItem.currentScale),
          gap: state.pairGap || 12,
        });
      }
    }
  }

  return {
    obstacles,
    spacingObstacle,
    isInstallerActive: !!(state.isInstallerFlowActive && state.isInstallerFlowActive()),
  };
}

export const homeRuntime = {
  computeInteraction,
  stepInstallerFlow,
};
